<template>
  <teleport to="body">
    <div class="overlay" @click.self="fecharModal">
      <div class="modal-encontros" @click.stop>
        <!-- Cabeçalho -->
        <div class="modal-header">
          <span class="modal-label">Encontros e Frequência</span>
          <button class="btn-fechar-modal" aria-label="Fechar" @click="fecharModal">
            ×
          </button>
        </div>

        <!-- Carregamento -->
        <div v-if="carregando" class="modal-encontros-body loading-state">
          <Carregamento />
        </div>

        <!-- Conteúdo -->
        <div v-else class="modal-encontros-body">
          <div v-if="atividade" class="container-tabela">
            <h4 class="nome-atividade">{{ atividade.nome }}</h4>

            <div class="atividade-meta">
              <span v-if="nomeSubtipo">
                <i class="ti ti-category"></i>
                {{ formatarEnum(nomeSubtipo) }}
              </span>

              <span>
                <i class="ti ti-world"></i>
                {{ formatarEnum(atividade.modalidadeExecucao ?? atividade.modalidade) }}
              </span>

              <span>
                <i class="ti ti-clipboard-list"></i>
                {{ formatarEnum(atividade.modalidadeInscricao) }}
              </span>

              <span>
                <i class="ti ti-calendar-time"></i>
                {{ formatarData(atividade.dataInicioInscricao) }} -
                {{ formatarData(atividade.dataFimInscricao) }}
              </span>
            </div>

            <div class="instrucao">
              <span>Selecione um encontro para realizar a frequência</span>
              <span class="contador-encontros">
                {{ encontros.length }}
                {{ encontros.length === 1 ? "encontro" : "encontros" }}
              </span>
            </div>

            <!-- Tabela de encontros -->
            <div v-if="encontros.length > 0" class="tabela-customizada">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Encontro</th>
                    <th>Horário</th>
                    <th>Data</th>
                    <th>Status da frequência</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(encontro, index) in encontros" :key="encontro.encontroId || index"
                    :class="{ desabilitado: !permiteFrequencia(encontro) }" @click="abrirCadastroFrequencia(encontro)">
                    <td class="col-ordem">{{ index + 1 }}</td>

                    <td class="col-encontro">
                      {{ encontro.titulo || encontro.descricao || `Encontro ${index + 1}` }}
                    </td>

                    <td class="col-horario">
                      {{ formatarDataHora(encontro.inicio_periodo).split(" ")[1] }}
                      -
                      {{ formatarDataHora(encontro.fim_periodo).split(" ")[1] }}
                    </td>

                    <td>{{ formatarData(encontro.fim_periodo) }}</td>

                    <td class="coluna-status">
                      <span v-if="
                        permiteFrequencia(encontro) &&
                        temFrequenciaCadastrada(encontro.encontroId)
                      " class="badge-status verde">
                        <i class="ti ti-check"></i>
                        Frequência cadastrada
                      </span>

                      <span v-else-if="
                        encontro.status === 'EM_EXECUCAO' ||
                        (encontro.status === 'FINALIZADO' && !isAtividadeEncerrada)
                      " class="badge-status amarelo">
                        <i class="ti ti-clock"></i>
                        Realizar frequência
                      </span>

                      <span v-else class="badge-status cinza">
                        {{ formatarStatusEncontro(encontro.status) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Nenhum encontro -->
            <div v-else class="sem-encontros">
              <i class="ti ti-calendar-off"></i>
              <p>Nenhum encontro encontrado para esta atividade.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, shallowRef } from "vue";
import { formatarData, formatarDataHora, formatarEnum } from "@/utils/FormatarEntradas";
import frequenciaService from "@/service/frequenciaService";
import router from "@/router";
import Carregamento from "@/components/common/Carregamento.vue";
import { useToast } from "@/composables/useToast";

/*
 * Os dados já vêm carregados pela página da atividade.
 * O modal não busca mais atividade, subtipo nem encontros.
 */
const props = defineProps({
  atividade: { type: Object, required: true },
  encontros: { type: Array, default: () => [] },
  nomeSubtipo: { type: String, default: "" },
});

const emit = defineEmits(["fechar"]);

const STATUS_ATIVIDADE_ENCERRADA = ["ENCERRADA", "CANCELADA", "FINALIZADO", "CONCLUIDA"];
const STATUS_ENCONTRO_COM_FREQUENCIA = ["EM_EXECUCAO", "FINALIZADO"];
const STATUS_FREQUENCIA_LANCADA = ["PRESENCA", "FALTA"];

const ROTULOS_STATUS_ENCONTRO = {
  EM_EXECUCAO: "Em Execução",
  PROGRAMADO: "Programado",
  FINALIZADO: "Finalizado",
  CANCELADO: "Cancelado",
  INSCRICOES_ABERTAS: "Inscrições Abertas",
};

const toast = useToast();

const carregando = ref(true);
// ids dos encontros que já têm frequência lançada (substituído de uma vez, por isso shallowRef)
const encontrosComFrequencia = shallowRef(new Set());

const isAtividadeEncerrada = computed(() =>
  STATUS_ATIVIDADE_ENCERRADA.includes(props.atividade.status)
);

const permiteFrequencia = (encontro) =>
  STATUS_ENCONTRO_COM_FREQUENCIA.includes(encontro.status);

const temFrequenciaCadastrada = (encontroId) =>
  encontrosComFrequencia.value.has(encontroId);

const formatarStatusEncontro = (status) => ROTULOS_STATUS_ENCONTRO[status] || status;

function fecharModal() {
  emit("fechar");
}

function possuiFrequenciaLancada(registros) {
  return (
    Array.isArray(registros) &&
    registros.some((registro) =>
      STATUS_FREQUENCIA_LANCADA.includes(registro?.statusFrequencia)
    )
  );
}

/*
 * Consulta só os encontros que aceitam frequência (os demais nunca usam esse dado)
 * e dispara todas as chamadas ao mesmo tempo.
 * Ideal: o backend devolver "frequenciaCadastrada" na listagem de encontros,
 * o que elimina este loop.
 */
async function carregarFrequencias() {
  const consultaveis = props.encontros.filter(permiteFrequencia);

  const respostas = await Promise.allSettled(
    consultaveis.map((encontro) => frequenciaService.getFrequencia(encontro.encontroId))
  );

  const comFrequencia = new Set();
  respostas.forEach((resposta, i) => {
    const { encontroId } = consultaveis[i];

    if (resposta.status === "rejected") {
      console.warn(`Erro ao carregar frequência do encontro ${encontroId}:`, resposta.reason);
    } else if (possuiFrequenciaLancada(resposta.value)) {
      comFrequencia.add(encontroId);
    }
  });

  encontrosComFrequencia.value = comFrequencia;
}

function abrirCadastroFrequencia(encontro) {
  if (!permiteFrequencia(encontro)) {
    toast.aviso(
      `Este encontro não está disponível para frequência. Status: ${formatarStatusEncontro(encontro.status)}`
    );
    return;
  }

  const { id: atividadeId, acaoId } = props.atividade;

  router.push(
    `/acao/${acaoId}/Atividades/${atividadeId}/cadastro-frequencia/${encontro.encontroId}`
  );
}

onMounted(async () => {
  document.body.classList.add("modal-aberto");

  try {
    await carregarFrequencias();
  } finally {
    carregando.value = false;
  }
});

onUnmounted(() => {
  document.body.classList.remove("modal-aberto");
});
</script>

<style scoped>
.modal-encontros {
  width: 100%;
  max-width: 960px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  box-shadow:
    0 20px 25px -5px hsla(0, 0%, 0%, 0.15),
    0 8px 10px -6px hsla(0, 0%, 0%, 0.08);
}

.modal-encontros-body {
  padding: 20px;
  background-color: var(--branco);
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.container-tabela {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.nome-atividade {
  margin: 0 0 10px;
  color: var(--roxo-primario);
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
}

.atividade-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 16px;
  margin-bottom: 24px;
  color: var(--preto);
  font-size: 13px;
}

.atividade-meta span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.atividade-meta i {
  color: var(--roxo-medio);
  font-size: 16px;
}

.instrucao {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  color: var(--preto);
  font-size: 14px;
  font-weight: 700;
}

.contador-encontros {
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: 99px;
  background-color: var(--cinza-claro);
  color: var(--cinza-medio);
  font-size: 12px;
  font-weight: 600;
}

.tabela-customizada {
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--cinza-claro);
  border-radius: 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

thead tr {
  background-color: var(--roxo-primario);
  color: var(--branco);
}

th {
  padding: 12px;
  border-right: 1px solid hsla(0, 0%, 100%, 0.15);
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

th:last-child {
  border-right: none;
}

tbody tr {
  background-color: var(--branco);
  border-bottom: 1px solid var(--cinza-claro);
  transition: background-color 0.15s ease;
}

tbody tr:last-child {
  border-bottom: none;
}

tbody tr:hover:not(.desabilitado) {
  background-color: var(--roxo-superficie);
}

tbody tr.desabilitado {
  cursor: not-allowed;
  opacity: 0.6;
}

tbody tr.desabilitado:hover {
  background-color: var(--branco);
}

td {
  padding: 12px;
  color: var(--preto);
  text-align: left;
  vertical-align: middle;
  cursor: pointer;
}

.col-ordem {
  width: 40px;
  color: var(--cinza-medio);
  font-weight: 600;
  text-align: center;
}

.col-encontro {
  min-width: 150px;
  font-weight: 500;
}

.col-horario {
  white-space: nowrap;
}

.coluna-status {
  min-width: 180px;
}

.badge-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 5px 9px;
  border-radius: 5px;
  color: var(--branco);
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}

.verde {
  background-color: var(--verde);
}

.amarelo {
  background-color: var(--laranja);
  color: var(--branco);
}

.cinza {
  background-color: var(--cinza-medio);
}

.sem-encontros {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px 16px;
  color: var(--cinza-medio);
  text-align: center;
}

.sem-encontros i {
  font-size: 36px;
  opacity: 0.6;
}

.sem-encontros p {
  margin: 0;
  font-size: 14px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

@media (max-width: 768px) {
  .overlay {
    padding: 8px;
  }

  .modal-encontros {
    max-width: 100%;
    max-height: 94vh;
    border-radius: 12px;
  }

  .modal-encontros-body {
    padding: 16px;
  }

  .atividade-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .instrucao {
    align-items: flex-start;
  }

  th,
  td {
    padding: 10px 8px;
  }
}

@media (max-width: 480px) {
  .instrucao {
    flex-direction: column;
  }
}
</style>